import type { MartinaEmailComposerReview } from "@shared/martinaEmailDraftApproval";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import DOMPurify from "dompurify";
import { buildEmailMessageHtml } from "@/lib/emailMessageRender";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { LayoutTemplate, Paperclip, X as XIcon } from "lucide-react";
import { Email } from "@/types";
import EmailTemplateManagerDialog from "@/components/email/EmailTemplateManagerDialog";
import { useEmailTemplates } from "@/hooks/useEmailTemplates";
import { apiRequest } from "@/lib/queryClient";
import {
  createEmailSendIdempotencyKey,
  canonicalEmailSendFeedback,
  sendCanonicalEmailThenLinkCrmAssociations,
  splitEmailRecipients,
} from "@/lib/emailCanonicalSend";
import { useToast } from "@/hooks/use-toast";
import { useEmailAccounts } from "@/hooks/useEmailAccounts";
import { useAuth } from "@/hooks/useAuth";
import { useUserIdentities } from "@/hooks/useUserProfile";
import { useEmailSignatures } from "@/hooks/useEmailSignatures";
import {
  applyEmailSignature,
  plainTextToEmailHtml,
  selectDefaultEmailSignature,
  stripAppliedEmailSignature,
  stripEmailSignatureEditorMarkers,
  trimMartinaDraftSignoffForSignature,
} from "@/lib/emailSignatureComposer";
import { CrmRecipientPicker } from "@/components/email/CrmRecipientPicker";
import { EmailMartinaComposerBar } from "@/components/email/EmailExAiComposerBar";
import { openMissingEmailContactCandidate } from "@/lib/emailRecipientContactCandidate";
import type { CanonicalEmailSendResult, EmailCrmAssociation } from "@/lib/emailCanonicalSend";
import { clearEmailComposerRecovery, readEmailComposerRecovery, writeEmailComposerRecovery, listEmailComposerRecoveryChoices, isEmailComposerRecoveryRetired, type EmailComposerRecoveryChoice, type EmailComposerRecovery } from "@/lib/emailComposerRecovery";

type EmailModalInitialData = {
  id: number;
  to: string[] | string;
  cc?: string[] | null;
  bcc?: string[] | null;
  [key: string]: any;
};

interface EmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: EmailModalInitialData | null;
  replyTo?: Email | null;
  forwardTo?: Email | null;
  draftBodyOverride?: string | null;
  replyMode?: 'reply' | 'reply_all';
  /** Chiamato dopo un invio riuscito, oltre alla chiusura del modale — per
   * riportare l'utente dove si trovava prima di aprire l'email (es. la
   * scheda del deal da cui l'ha aperta), invece di lasciarlo nel lettore
   * generico. */
  onSent?: (result: CanonicalEmailSendResult) => void;
  initialCompose?: {
    to?: string[];
    subject?: string;
    /** Bozza iniziale esplicita da una superficie CRM, senza invio automatico. */
    body?: string;
    contactId?: number | null;
    companyId?: number | null;
    dealId?: number | null;
    leadId?: number | null;
    branchId?: number | null;
  } | null;
}

const emailSchema = z.object({
  subject: z.string().min(1, "L'oggetto e' obbligatorio"),
  body: z.string().min(1, "Il corpo dell'email e' obbligatorio"),
  from: z.string().email("L'account mittente deve essere un indirizzo email valido"),
  to: z.array(z.string().email("Ogni destinatario deve essere un indirizzo email valido")).min(1, "Inserisci almeno un destinatario"),
  cc: z.array(z.string().email("Ogni destinatario CC deve essere un indirizzo email valido")).optional(),
  bcc: z.array(z.string().email("Ogni destinatario BCC deve essere un indirizzo email valido")).optional(),
  accountId: z.number({
    required_error: "Seleziona un account mittente",
    invalid_type_error: "Seleziona un account mittente",
  }),
  contactId: z.number().nullable().optional(),
  companyId: z.number().nullable().optional(),
  dealId: z.number().nullable().optional(),
  leadId: z.number().nullable().optional(),
  branchId: z.number().nullable().optional(),
});

type EmailFormData = z.infer<typeof emailSchema>;

type ComposerAttachment = {
  id: string;
  filename: string;
  contentBase64?: string;
  contentType: string;
  size: number;
  persistedId?: number;
};

function persistedDraftAttachments(email: EmailModalInitialData | null | undefined): ComposerAttachment[] {
  const candidates = (email as any)?.attachments;
  if (!Array.isArray(candidates)) return [];
  return candidates.flatMap((attachment: any): ComposerAttachment[] => {
    const persistedId = Number(attachment?.id);
    if (!Number.isSafeInteger(persistedId) || persistedId <= 0
      || attachment?.related || attachment?.contentDisposition === 'inline') return [];
    return [{
      id: `persisted-${persistedId}`,
      persistedId,
      filename: String(attachment?.filename || 'Allegato'),
      contentType: String(attachment?.contentType || 'application/octet-stream'),
      size: Math.max(0, Number(attachment?.size) || 0),
    }];
  });
}

const NO_SIGNATURE_VALUE = "none";

let ephemeralComposerTabId: string | null = null;
function composerTabId(): string {
  if (ephemeralComposerTabId) return ephemeralComposerTabId;
  try {
    const stored = window.sessionStorage.getItem('experviser:email-composer-tab-id');
    if (stored) return (ephemeralComposerTabId = stored);
    const created = crypto.randomUUID();
    window.sessionStorage.setItem('experviser:email-composer-tab-id', created);
    return (ephemeralComposerTabId = created);
  } catch { return (ephemeralComposerTabId = crypto.randomUUID()); }
}

function formatAttachmentSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function userFacingApiError(error: unknown): string {
  const fallback = error instanceof Error ? error.message : String(error);
  const match = fallback.match(/HTTP\s+\d+:\s*(\{[\s\S]*\})/);
  if (!match) return fallback;
  try {
    const parsed = JSON.parse(match[1]);
    return typeof parsed?.error === 'string' ? parsed.error : fallback;
  } catch {
    return fallback;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isHtmlLike(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function textToParagraphHtml(value: string): string {
  return plainTextToEmailHtml(value);
}

function normalizeMartinaDraftForComposer(value: string): string {
  return isHtmlLike(value)
    ? DOMPurify.sanitize(value, { FORBID_ATTR: ['data-experviser-signature-boundary', 'data-signature-id'] })
    : textToParagraphHtml(value);
}

function getEmailHtmlBody(email: any): string {
  const html = email?.html || email?.bodyHtml || email?.body_html || (email?.bodyType === 'text/html' ? email?.body : '');
  if (typeof html === 'string' && html.trim()) return html;

  const text = email?.text || email?.plainText || email?.plain_text || email?.body || '';
  return typeof text === 'string' && text.trim()
    ? (isHtmlLike(text) ? text : textToParagraphHtml(text))
    : '';
}

function buildQuotedEmailHtml(email: any, label: string): string {
  const date = email?.receivedAt || email?.date || new Date().toISOString();
  const subject = email?.subject || '';
  const body = getEmailHtmlBody(email);
  return [
    '<div class="experviser-email-quote" style="margin-top:16px;border-left:3px solid #d1d5db;padding-left:12px;color:#374151;">',
    `<p style="margin:0 0 8px 0;font-size:12px;color:#6b7280;">${escapeHtml(label)}</p>`,
    '<div style="margin:0 0 12px 0;font-size:12px;color:#6b7280;">',
    `<div><strong>Da:</strong> ${escapeHtml(String(email?.from || ''))}</div>`,
    `<div><strong>Data:</strong> ${escapeHtml(new Date(date).toLocaleString())}</div>`,
    `<div><strong>Oggetto:</strong> ${escapeHtml(String(subject))}</div>`,
    '</div>',
    `<div>${body}</div>`,
    '</div>',
  ].join('');
}

function getQuotedEmailHtml(replyTo: any, forwardTo: any): string {
  if (replyTo) return buildQuotedEmailHtml(replyTo, '---------- Messaggio originale ----------');
  if (forwardTo) return buildQuotedEmailHtml(forwardTo, '---------- Messaggio inoltrato ----------');
  return '';
}

function normalizeEmailValue(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function uniqueEmails(values: unknown[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const email = typeof value === 'string' ? value.trim() : '';
    const key = normalizeEmailValue(email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}

export function replyRecipientsForEmail(email: any, accounts: any[] | undefined, identities: Array<{ email?: string | null }> | undefined): string[] {
  const accountEmails = new Set([
    ...(accounts ?? []).map((account) => normalizeEmailValue(account?.email)),
    ...(identities ?? []).map((identity) => normalizeEmailValue(identity?.email)),
  ].filter(Boolean));
  const from = normalizeEmailValue(email?.from);
  if (from && !accountEmails.has(from)) return uniqueEmails([email.from]);

  return uniqueEmails([
    ...(Array.isArray(email?.to) ? email.to : [email?.to]),
    ...(Array.isArray(email?.cc) ? email.cc : []),
  ]).filter((recipient) => !accountEmails.has(normalizeEmailValue(recipient)));
}

export function replyAllRecipientsForEmail(email: any, accounts: any[] | undefined, identities: Array<{ email?: string | null }> | undefined): { to: string[]; cc: string[] } {
  const accountEmails = new Set([
    ...(accounts ?? []).map((account) => normalizeEmailValue(account?.email)),
    ...(identities ?? []).map((identity) => normalizeEmailValue(identity?.email)),
  ].filter(Boolean));
  const externalFrom = accountEmails.has(normalizeEmailValue(email?.from)) ? [] : uniqueEmails([email?.from]);
  const originalTo = uniqueEmails(Array.isArray(email?.to) ? email.to : [email?.to])
    .filter((recipient) => !accountEmails.has(normalizeEmailValue(recipient)));
  const originalCc = uniqueEmails(Array.isArray(email?.cc) ? email.cc : [])
    .filter((recipient) => !accountEmails.has(normalizeEmailValue(recipient)));
  const to = externalFrom.length ? externalFrom : originalTo;
  const toKeys = new Set(to.map(normalizeEmailValue));
  return { to, cc: uniqueEmails([...originalTo, ...originalCc]).filter((recipient) => !toKeys.has(normalizeEmailValue(recipient))) };
}

function getAccountLabel(account: any): string {
  return account?.displayName || account?.display_name || account?.name || account?.email || "Account email";
}

function getAccountEmail(account: any): string {
  return account?.email || "";
}

interface DealOption {
  id: number;
  name?: string | null;
  title?: string | null;
}

function normalizeDeals(data: unknown): DealOption[] {
  const raw = Array.isArray(data) ? data : (data as any)?.data;
  return Array.isArray(raw) ? raw : [];
}

function dealLabel(deal?: DealOption | null): string {
  return deal?.name || deal?.title || "Opportunita senza nome";
}

function activeCrmAssociationsFromEmail(email?: any | null): EmailCrmAssociation[] {
  if (!email) return [];
  const candidates = [
    email.crmAssociations,
    email.crm_associations,
    email.associations?.crm,
  ];
  return candidates
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((association): association is EmailCrmAssociation => (
      association
      && association.status !== "deleted"
      && Number.isSafeInteger(Number(association.entityId))
      && Number(association.entityId) > 0
    ));
}

function associatedEntityIdFromEmail(email: any | null | undefined, entityType: EmailCrmAssociation["entityType"], legacyKeys: string[] = []): number | null {
  const association = activeCrmAssociationsFromEmail(email).find((item) => item.entityType === entityType);
  const associationId = Number(association?.entityId);
  if (Number.isSafeInteger(associationId) && associationId > 0) return associationId;

  for (const key of legacyKeys) {
    const value = email?.[key];
    const legacyValue = Array.isArray(value) ? value[0] : value;
    const id = Number(legacyValue);
    if (Number.isSafeInteger(id) && id > 0) return id;
  }

  return null;
}

export default function EmailModal({
  open,
  onOpenChange,
  initialData,
  replyTo,
  forwardTo,
  draftBodyOverride,
  replyMode = 'reply',
  initialCompose,
  onSent,
}: EmailModalProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [dealSearch, setDealSearch] = useState("");
  const [selectedSignatureId, setSelectedSignatureId] = useState(NO_SIGNATURE_VALUE);
  const [signatureContextApplied, setSignatureContextApplied] = useState("");
  const manualSignatureSelectionRef = useRef(false);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const initialPersistedAttachmentIdsRef = useRef<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftIdempotencyKeyRef = useRef<string | null>(null);
  const sendIdempotencyKeyRef = useRef<string | null>(null);
  const draftMessageIdRef = useRef<number | null>(null);
  const openIdentityRef = useRef<string | null>(null);
  const draftVersionRef = useRef<string | null>(null);
  const lastSavedSignatureRef = useRef<string | null>(null);
  const pendingDraftSaveRef = useRef(false);
  const saveDraftRef = useRef<((manual?: boolean, wait?: boolean) => Promise<boolean>) | null>(null);
  const isSavingDraftRef = useRef(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const composerReviewRef = useRef<(draft: string) => MartinaEmailComposerReview | null>(() => null);
  useEffect(() => () => { composerReviewRef.current = () => null; }, []);
  const [isRecoveryReady, setIsRecoveryReady] = useState(false);
  const [recoveryChoices, setRecoveryChoices] = useState<EmailComposerRecoveryChoice[]>([]);
  const [draftSaveState, setDraftSaveState] = useState<'saving' | 'saved' | 'local' | 'conflict'>('local');
  const draftSavePromiseRef = useRef<Promise<boolean> | null>(null);
  const [isDraftInTrash, setIsDraftInTrash] = useState(Boolean((initialData as any)?.isTrash));
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateTrackOpens, setTemplateTrackOpens] = useState(true);

  useEffect(() => {
    const identity = open ? `${initialData?.id ?? 'new'}:${replyTo?.id ?? ''}:${forwardTo?.id ?? ''}` : null;
    if (openIdentityRef.current === identity) return;
    openIdentityRef.current = identity;
    const existingDraftId = open && initialData && ((initialData as any).isDraft || (initialData as any).folder === 'draft')
      ? Number(initialData.id)
      : null;
    draftMessageIdRef.current = Number.isSafeInteger(existingDraftId) && Number(existingDraftId) > 0 ? existingDraftId : null;
    draftVersionRef.current = draftMessageIdRef.current && (initialData as any)?.updatedAt
      ? new Date((initialData as any).updatedAt).toISOString() : null;
    lastSavedSignatureRef.current = null;
    setIsRecoveryReady(false);
    setIsDraftInTrash(Boolean((initialData as any)?.isTrash || (initialData as any)?.folder === 'trash'));
    draftIdempotencyKeyRef.current = open
      ? draftMessageIdRef.current ? `draft-message-${draftMessageIdRef.current}` : createEmailSendIdempotencyKey('draft')
      : null;
    sendIdempotencyKeyRef.current = open ? createEmailSendIdempotencyKey('modal-send') : null;
    const restoredAttachments = open && draftMessageIdRef.current ? persistedDraftAttachments(initialData) : [];
    initialPersistedAttachmentIdsRef.current = restoredAttachments.flatMap(attachment => attachment.persistedId ? [attachment.persistedId] : []);
    setAttachments(restoredAttachments);
    setSelectedTemplateId(open ? Number((initialData as any)?.templateId) || null : null);
    setTemplateTrackOpens(open ? (initialData as any)?.templateTrackOpens ?? true : true);
    if (!open) setTemplateManagerOpen(false);
  }, [open, initialData]);

  const handleAttachFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: 'File troppo grande', description: `${file.name} supera 10 MB`, variant: 'destructive' });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64 = result.split(',')[1];
        setAttachments((prev) => [...prev, { id: `${file.name}-${Date.now()}`, filename: file.name, contentBase64: base64, contentType: file.type || 'application/octet-stream', size: file.size }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const { data: accounts, isLoading: isLoadingAccounts } = useEmailAccounts();
  const { data: internalIdentities } = useUserIdentities();
  const templatesQuery = useEmailTemplates(open);
  const { data: dealsData } = useQuery({
    queryKey: ["/api/deals", "email-modal-deal-picker"],
    queryFn: () => apiRequest("/api/deals"),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      subject: "",
      body: "",
      from: "",
      to: [],
      cc: [],
      bcc: [],
      accountId: undefined,
      contactId: null,
      companyId: null,
      dealId: null,
      leadId: null,
      branchId: null,
    }
  });

  const retainedAttachmentIds = attachments.flatMap(attachment => attachment.persistedId ? [attachment.persistedId] : []);
  const attachmentEdits = attachments.some(attachment => !attachment.persistedId)
    || [...retainedAttachmentIds].sort((a, b) => a - b).join(',')
      !== [...initialPersistedAttachmentIdsRef.current].sort((a, b) => a - b).join(',');
  const unsavedComposerEdits = draftSaveState !== 'saved' && (form.formState.isDirty || attachmentEdits
    || toInput.trim() !== toRecipients.join(", ").trim()
    || ccInput.trim() !== ccRecipients.join(", ").trim()
    || bccInput.trim() !== bccRecipients.join(", ").trim());
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(open, unsavedComposerEdits, onOpenChange);

  // Owner-scoped local recovery includes pending attachments. The server remains
  // authoritative; this copy only covers interruptions and offline edits.
  const recoveryContext = `${forwardTo ? "forward" : replyTo ? "reply" : "compose"}:${forwardTo?.id ?? replyTo?.id ?? initialData?.id ?? initialCompose?.to?.join(",") ?? "new"}`;
  const draftRecoveryKey = `experviser:composer-recovery:v2:${currentUser?.id ?? 'pending'}:${composerTabId()}:${recoveryContext}`;
  const recoveredDraftKey = useRef<string | null>(null);
  const watchedDraft = form.watch();
  const restoreLocalDraft = (parsed: Partial<EmailComposerRecovery>) => {
    if (Number.isSafeInteger(parsed.draftMessageId) && Number(parsed.draftMessageId) > 0) draftMessageIdRef.current = Number(parsed.draftMessageId);
    if (typeof parsed.draftUpdatedAt === 'string') draftVersionRef.current = parsed.draftUpdatedAt;
    if (typeof parsed.draftIdempotencyKey === 'string') draftIdempotencyKeyRef.current = parsed.draftIdempotencyKey;
    if (typeof parsed.sendIdempotencyKey === 'string') sendIdempotencyKeyRef.current = parsed.sendIdempotencyKey;
    if (Number.isSafeInteger(parsed.accountId) && Number(parsed.accountId) > 0
      && accounts?.some(account => account.id === parsed.accountId)) {
      form.setValue('accountId', Number(parsed.accountId), { shouldDirty: true });
      form.setValue('from', String(parsed.from || accounts?.find(account => account.id === parsed.accountId)?.email || ''), { shouldDirty: true });
    }
    if (typeof parsed.subject === "string") form.setValue("subject", parsed.subject, { shouldDirty: true });
    if (typeof parsed.body === "string") form.setValue("body", parsed.body, { shouldDirty: true });
    if (typeof parsed.toInput === "string") setToInput(parsed.toInput);
    if (typeof parsed.ccInput === "string") setCcInput(parsed.ccInput);
    if (typeof parsed.bccInput === "string") setBccInput(parsed.bccInput);
    if (Array.isArray(parsed.toRecipients)) setToRecipients(parsed.toRecipients.filter((x: unknown) => typeof x === "string"));
    if (Array.isArray(parsed.ccRecipients)) setCcRecipients(parsed.ccRecipients.filter((x: unknown) => typeof x === "string"));
    if (Array.isArray(parsed.bccRecipients)) setBccRecipients(parsed.bccRecipients.filter((x: unknown) => typeof x === "string"));
    if (Array.isArray(parsed.attachments)) setAttachments(parsed.attachments as ComposerAttachment[]);
    setDraftSaveState('local');
    setIsRecoveryReady(true);
    toast({ title: "Bozza locale recuperata", description: "Controlla il contenuto e gli allegati prima dell'invio." });
    setRecoveryChoices([]);
  };
  useEffect(() => {
    if (!open) {
      recoveredDraftKey.current = null;
      setRecoveryChoices([]);
      return;
    }
    if (recoveredDraftKey.current === draftRecoveryKey || !Array.isArray(accounts) || !currentUser?.id) return;
    recoveredDraftKey.current = draftRecoveryKey;
    let cancelled = false;
    void (async () => {
      let parsed: Partial<EmailComposerRecovery> | null = null;
      try { parsed = await readEmailComposerRecovery(draftRecoveryKey); } catch { /* browser storage may be restricted */ }
      if (!parsed) {
        try { parsed = JSON.parse(window.sessionStorage.getItem(draftRecoveryKey) || 'null'); } catch { /* restricted storage */ }
      }
      // v1 had no owner. Preserve it locally, but never import its content
      // into whichever authenticated user's composer happens to open next.
      if (parsed?.draftIdempotencyKey) {
        try { if (await isEmailComposerRecoveryRetired(draftRecoveryKey, parsed.draftIdempotencyKey)) parsed = null; } catch { /* storage-restricted text fallback remains local */ }
      }
      if (cancelled) return;
      const serverUpdatedAt = initialData && (initialData as any).updatedAt
        ? new Date((initialData as any).updatedAt).getTime() : 0;
      if (!parsed || !Number.isFinite(parsed.savedAt) || Number(parsed.savedAt) <= serverUpdatedAt) {
        let choices: EmailComposerRecoveryChoice[] = [];
        try { choices = await listEmailComposerRecoveryChoices(currentUser.id, recoveryContext, draftRecoveryKey); } catch { /* keep normal composer usable if local storage is restricted */ }
        if (cancelled) return;
        if (choices.length) { setRecoveryChoices(choices); return; }
        setIsRecoveryReady(true);
        return;
      }
      restoreLocalDraft(parsed);
    })();
    return () => { cancelled = true; };
  }, [open, draftRecoveryKey, accounts, currentUser?.id, form, toast]);

  useEffect(() => {
    if (!open || !isRecoveryReady || recoveredDraftKey.current !== draftRecoveryKey) return;
    const snapshot: EmailComposerRecovery = {
      savedAt: Date.now(), pending: draftSaveState !== 'saved', draftMessageId: draftMessageIdRef.current,
      draftUpdatedAt: draftVersionRef.current,
      draftIdempotencyKey: draftIdempotencyKeyRef.current ?? createEmailSendIdempotencyKey('draft'),
      sendIdempotencyKey: sendIdempotencyKeyRef.current ?? createEmailSendIdempotencyKey('modal-send'),
      accountId: Number.isSafeInteger(watchedDraft.accountId) ? watchedDraft.accountId : null,
      from: watchedDraft.from || '',
      subject: watchedDraft.subject || '', body: watchedDraft.body || '',
      toInput, ccInput, bccInput, toRecipients, ccRecipients, bccRecipients, attachments,
    };
    const persist = () => {
      try {
        window.sessionStorage.setItem(draftRecoveryKey, JSON.stringify({ ...snapshot, attachments: [] }));
      } catch { /* IndexedDB remains available on most browsers */ }
      // Il recupero locale è facoltativo e non cambia l'esito del writer.
      void writeEmailComposerRecovery(draftRecoveryKey, snapshot).catch(() => {});
    };
    persist();
    window.addEventListener('pagehide', persist);
    return () => window.removeEventListener('pagehide', persist);
  }, [open, isRecoveryReady, draftRecoveryKey, watchedDraft.subject, watchedDraft.body, toInput, ccInput, bccInput, toRecipients, ccRecipients, bccRecipients, attachments, draftSaveState]);

  const clearRecoveredDraft = () => {
    try { window.sessionStorage.removeItem(draftRecoveryKey); } catch { /* optional storage */ }
    void clearEmailComposerRecovery(draftRecoveryKey, draftIdempotencyKeyRef.current).catch(() => {});
  };
  const requestEmailClose = (nextOpen: boolean) => {
    return requestClose(nextOpen);
  };

  const selectedAccountId = form.watch("accountId");
  const selectedDealId = form.watch("dealId");
  const { signatures, isLoading: isLoadingSignatures } = useEmailSignatures(open ? selectedAccountId ?? null : null);
  const dealOptions = normalizeDeals(dealsData);
  const selectedDeal = dealOptions.find((deal) => Number(deal.id) === Number(selectedDealId)) ?? null;
  const normalizedDealSearch = dealSearch.trim().toLowerCase();
  const matchingDealOptions = dealOptions
    .filter((deal) => {
      const id = Number(deal.id);
      if (!Number.isSafeInteger(id) || id <= 0 || id === Number(selectedDealId)) return false;
      if (!normalizedDealSearch) return false;
      return [deal.name, deal.title, `deal ${deal.id}`, `opportunita ${deal.id}`]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedDealSearch);
    })
    .slice(0, 6);
  const hasSendAccount = Array.isArray(accounts) && accounts.length > 0;
  const signatureMode = replyTo || forwardTo ? "reply" : "new";
  const defaultSignature = Array.isArray(signatures)
    ? selectDefaultEmailSignature(signatures, signatureMode)
    : null;
  const signatureOptions = [
    { id: NO_SIGNATURE_VALUE, name: "Nessuna firma" },
    ...(Array.isArray(signatures)
      ? signatures.map((signature) => ({ id: signature.id.toString(), name: signature.name }))
      : []),
  ];
  const applyTemplateSelection = (templateId: number | null) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setTemplateTrackOpens(true);
      return;
    }
    const template = templatesQuery.data?.find((item) => item.id === templateId);
    if (!template) return;
    const templateSignature = template.defaultSignatureId
      ? signatures?.find((signature) => signature.id === template.defaultSignatureId) ?? defaultSignature
      : defaultSignature;
    manualSignatureSelectionRef.current = true;
    setSelectedSignatureId(templateSignature?.id.toString() ?? NO_SIGNATURE_VALUE);
    setTemplateTrackOpens(template.trackOpens);
    form.setValue("subject", template.subject, { shouldDirty: true, shouldValidate: true });
    form.setValue("body", applyEmailSignature(template.bodyHtml, templateSignature), { shouldDirty: true, shouldValidate: true });
    toast({
      title: "Modello applicato",
      description: template.separateUntilReply
        ? "Dopo l'invio il messaggio resterà in Attesa risposta finché non arriva una risposta reale."
        : "Contenuto inserito nel composer; l'invio resta manuale.",
    });
  };
  const prepareMartinaReplyForSignature = (body: string, signature: typeof defaultSignature | undefined): string => {
    const draft = draftBodyOverride?.trim();
    if (!replyTo || !draft || !signature) return body;
    const originalDraftBody = `${normalizeMartinaDraftForComposer(draft)}<p><br></p>`;
    // A delayed signature fetch must not rewrite an answer Michele edited while
    // the composer was open. The source draft is the only text normalized.
    if (stripAppliedEmailSignature(body) !== originalDraftBody) return body;
    const prepared = trimMartinaDraftSignoffForSignature(draft, signature);
    return prepared === draft ? body : `${normalizeMartinaDraftForComposer(prepared)}<p><br></p>`;
  };
  const modalMode = forwardTo ? "forward" : replyTo ? "reply" : "compose";
  const quotedEmailHtml = getQuotedEmailHtml(replyTo, forwardTo);
  const quotedSource = replyTo || forwardTo;
  const isInitialDraft = Boolean(initialData && ((initialData as any).isDraft || (initialData as any).folder === 'draft'));
  const sourceMessageIdCandidate = Number(quotedSource?.id ?? (isInitialDraft && !forwardTo ? (initialData as any)?.sourceMessageId : null));
  const composerSourceMessageId = Number.isSafeInteger(sourceMessageIdCandidate) && sourceMessageIdCandidate > 0
    && sourceMessageIdCandidate !== draftMessageIdRef.current ? sourceMessageIdCandidate : null;
  // Every draft has a thread container. Only a real source message makes it
  // a reply: reopening a new-message draft must still use the compose sender.
  const threadIdCandidate = Number(forwardTo || (isInitialDraft && !composerSourceMessageId)
    ? null
    : (replyTo as any)?.threadId ?? (replyTo as any)?.thread_id
      ?? (initialData as any)?.threadId ?? (initialData as any)?.thread_id);
  const composerThreadId = Number.isSafeInteger(threadIdCandidate) && threadIdCandidate > 0 ? threadIdCandidate : null;
  const quotedPreviewHtml = quotedEmailHtml ? buildEmailMessageHtml({
    html: quotedEmailHtml, id: quotedSource?.id, attachments: (quotedSource as any)?.attachments,
  }) : "";
  const modalTitle = modalMode === "forward"
    ? "Inoltra email"
    : modalMode === "reply"
      ? "Rispondi"
      : "Componi email";
  const submitLabel = modalMode === "forward"
    ? "Invia inoltro"
    : modalMode === "reply"
      ? "Invia risposta"
      : "Invia email";

  // Set default account if available
  useEffect(() => {
    if (accounts && accounts.length > 0 && !form.getValues("accountId")) {
      const primary = accounts.find((account) => account.isPrimary) || accounts[0];
      form.setValue("accountId", primary.id);
      form.setValue("from", primary.email);
    }
  }, [accounts, form]);

  // Update form when initialData, replyTo or forwardTo changes
  useEffect(() => {
    // Late account/identity fetches must never replace user-entered text.
    if (open && form.formState.isDirty) return;
    if (initialData) {
      // Edit existing email
      form.reset(({
        ...initialData,
        to: Array.isArray(initialData.to) ? initialData.to : [initialData.to].filter(Boolean) as unknown as string[],
        cc: (initialData.cc || []) as string[],
        bcc: (initialData.bcc || []) as string[],
      }) as any);

      setToInput(Array.isArray(initialData.to) ? initialData.to.join(", ") : (initialData.to || ""));
      setCcInput(initialData.cc?.join(", ") || "");
      setBccInput(initialData.bcc?.join(", ") || "");
      setToRecipients(Array.isArray(initialData.to) ? initialData.to : [initialData.to].filter(Boolean) as unknown as string[]);
      setCcRecipients((initialData.cc || []) as string[]);
      setBccRecipients((initialData.bcc || []) as string[]);
      setSelectedSignatureId((initialData as any).signatureId ? String((initialData as any).signatureId) : NO_SIGNATURE_VALUE);
      manualSignatureSelectionRef.current = Boolean((initialData as any).templateId);
      setSignatureContextApplied("");
    } else if (forwardTo) {
      const originalSubject = forwardTo.subject || '';
      const subject = originalSubject.startsWith("Fwd:")
        ? originalSubject
        : `Fwd: ${originalSubject}`;
      form.reset({
        subject,
        body: '<p><br></p>',
        from: accounts?.find((account) => account.id === forwardTo.accountId)?.email || accounts?.find((account) => account.isPrimary)?.email || accounts?.[0]?.email || "",
        to: [],
        cc: [],
        bcc: [],
        accountId: forwardTo.accountId || accounts?.find((account) => account.isPrimary)?.id || accounts?.[0]?.id,
        contactId: associatedEntityIdFromEmail(forwardTo, "contact", ["contactId", "contact_id"]),
        companyId: associatedEntityIdFromEmail(forwardTo, "company", ["companyId", "company_id"]),
        dealId: associatedEntityIdFromEmail(forwardTo, "deal", ["dealId", "deal_id", "dealIds", "deal_ids"]),
        leadId: associatedEntityIdFromEmail(forwardTo, "lead", ["leadId", "lead_id"]),
        branchId: associatedEntityIdFromEmail(forwardTo, "branch", ["branchId", "branch_id"]),
      });

      setToInput("");
      setCcInput("");
      setBccInput("");
      setToRecipients([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setDealSearch("");
      setSelectedSignatureId(NO_SIGNATURE_VALUE);
      manualSignatureSelectionRef.current = false;
      setSignatureContextApplied("");
    } else if (replyTo) {
      // Reply to email
      const replySubject = replyTo.subject || '';
      const subject = replySubject.startsWith("Re:")
        ? replySubject
        : `Re: ${replySubject}`;
      const draftBody = draftBodyOverride?.trim() || '';
      const draftedReply = draftBody
        ? `${normalizeMartinaDraftForComposer(draftBody)}<p><br></p>`
        : '';
      const replyAllRecipients = replyAllRecipientsForEmail(replyTo, accounts, internalIdentities);
      const replyRecipients = replyMode === 'reply_all' ? replyAllRecipients.to : replyRecipientsForEmail(replyTo, accounts, internalIdentities);
      const replyCcRecipients = replyMode === 'reply_all' ? replyAllRecipients.cc : [];

      form.reset({
        subject,
        body: draftedReply || '<p><br></p>',
        from: accounts?.find((account) => account.id === replyTo.accountId)?.email || accounts?.find((account) => account.isPrimary)?.email || accounts?.[0]?.email || "",
        to: replyRecipients,
        cc: replyCcRecipients,
        bcc: [],
        accountId: replyTo.accountId || accounts?.find((account) => account.isPrimary)?.id || accounts?.[0]?.id,
        contactId: associatedEntityIdFromEmail(replyTo, "contact", ["contactId", "contact_id"]),
        companyId: associatedEntityIdFromEmail(replyTo, "company", ["companyId", "company_id"]),
        dealId: associatedEntityIdFromEmail(replyTo, "deal", ["dealId", "deal_id", "dealIds", "deal_ids"]),
        leadId: associatedEntityIdFromEmail(replyTo, "lead", ["leadId", "lead_id"]),
        branchId: associatedEntityIdFromEmail(replyTo, "branch", ["branchId", "branch_id"]),
      });

      setToInput(replyRecipients.join(", "));
      setCcInput(replyCcRecipients.join(", "));
      setBccInput("");
      setToRecipients(replyRecipients);
      setCcRecipients(replyCcRecipients);
      setBccRecipients([]);
      setDealSearch("");
      setSelectedSignatureId(NO_SIGNATURE_VALUE);
      manualSignatureSelectionRef.current = false;
      setSignatureContextApplied("");
    } else {
      const primary = accounts?.find((account) => account.isPrimary) || accounts?.[0];
      const nextTo = initialCompose?.to?.filter(Boolean) ?? [];
      // New email
      form.reset({
        subject: initialCompose?.subject || "",
        body: initialCompose?.body || "",
        from: primary?.email || "",
        to: nextTo,
        cc: [],
        bcc: [],
        accountId: primary?.id,
        contactId: initialCompose?.contactId ?? null,
        companyId: initialCompose?.companyId ?? null,
        dealId: initialCompose?.dealId ?? null,
        leadId: initialCompose?.leadId ?? null,
        branchId: initialCompose?.branchId ?? null,
      });

      setToInput(nextTo.join(", "));
      setCcInput("");
      setBccInput("");
      setToRecipients(nextTo);
      setCcRecipients([]);
      setBccRecipients([]);
      setDealSearch("");
      setSelectedSignatureId(NO_SIGNATURE_VALUE);
      manualSignatureSelectionRef.current = false;
      setSignatureContextApplied("");
    }
  }, [initialData, replyTo, forwardTo, draftBodyOverride, replyMode, initialCompose, form, accounts, internalIdentities]);

  const applySignatureSelection = (signatureId: string) => {
    const signature = signatureId === NO_SIGNATURE_VALUE || !Array.isArray(signatures)
      ? null
      : signatures.find((item) => item.id === Number(signatureId));

    form.setValue("body", applyEmailSignature(prepareMartinaReplyForSignature(form.getValues("body"), signature), signature), {
      shouldDirty: true,
      shouldValidate: true,
    });
    setSelectedSignatureId(signatureId);
  };

  useEffect(() => {
    if (!open || !selectedAccountId || !Array.isArray(signatures)) return;
    if (isLoadingSignatures) return;
    if (manualSignatureSelectionRef.current) return;

    const contextSource = replyTo?.id ?? forwardTo?.id ?? initialData?.id ?? "new";
    const nextSignatureId = defaultSignature ? defaultSignature.id.toString() : NO_SIGNATURE_VALUE;
    const contextKey = `${signatureMode}:${selectedAccountId}:${contextSource}:${nextSignatureId}`;
    if (signatureContextApplied === contextKey) return;

    form.setValue("body", applyEmailSignature(prepareMartinaReplyForSignature(form.getValues("body"), defaultSignature), defaultSignature), {
      shouldDirty: false,
      shouldValidate: true,
    });
    setSelectedSignatureId(nextSignatureId);
    setSignatureContextApplied(contextKey);
  }, [
    defaultSignature,
    form,
    forwardTo?.id,
    initialData?.id,
    isLoadingSignatures,
    open,
    replyTo?.id,
    selectedAccountId,
    signatures,
    signatureContextApplied,
    signatureMode,
  ]);

  // Process recipients input into arrays
  const processRecipients = () => {
    form.setValue("to", toRecipients);
    form.setValue("cc", ccRecipients);
    form.setValue("bcc", bccRecipients);
  };

  // Handle account change to update the from field
  const handleAccountChange = (accountId: number) => {
    if (composerThreadId && composerSourceMessageId && accountId !== form.getValues('accountId')) {
      toast({
        title: 'Mittente della risposta',
        description: 'Questa risposta usa la casella del messaggio originale. Il contenuto resta conservato in Bozze.',
        variant: 'destructive',
      });
      return;
    }
    const account = accounts?.find(a => a.id === accountId);
    if (account) {
      manualSignatureSelectionRef.current = false;
      setSelectedSignatureId(NO_SIGNATURE_VALUE);
      form.setValue("from", account.email);
    }
    form.setValue("accountId", accountId);
    setSignatureContextApplied("");
  };

  const sendEmail = useMutation({
    mutationFn: async (data: EmailFormData) => {
      const isForward = Boolean(forwardTo);
      const newAttachments = draftMessageIdRef.current ? [] : attachments.filter(attachment => attachment.contentBase64);
      const emailData = {
        ...(composerSourceMessageId ? { sourceMessageId: composerSourceMessageId } : {}),
        ...(draftMessageIdRef.current ? { draftMessageId: draftMessageIdRef.current } : {}),
        accountId: data.accountId,
        idempotencyKey: sendIdempotencyKeyRef.current ?? createEmailSendIdempotencyKey(isForward ? 'modal-forward' : composerThreadId ? 'modal-reply' : 'modal-compose'),
        to: data.to,
        cc: data.cc || [],
        bcc: data.bcc || [],
        subject: data.subject,
        text: stripHtml(`${stripEmailSignatureEditorMarkers(data.body)}${quotedEmailHtml}`),
        html: `${stripEmailSignatureEditorMarkers(data.body)}${quotedEmailHtml}`,
        ...(selectedTemplateId ? {
          templateId: selectedTemplateId,
          signatureId: selectedSignatureId === NO_SIGNATURE_VALUE ? null : Number(selectedSignatureId),
          trackOpens: templateTrackOpens,
        } : {}),
        ...(newAttachments.length > 0 && { attachments: newAttachments.map((attachment) => ({
          filename: attachment.filename,
          contentBase64: attachment.contentBase64!,
          contentType: attachment.contentType,
          size: attachment.size,
        })) }),
      };

      const endpoint = composerThreadId ? `/api/email/threads/${composerThreadId}/reply` : "/api/email/send";
      return sendCanonicalEmailThenLinkCrmAssociations({
        endpoint,
        payload: emailData,
        associations: [
          { entityType: 'contact', entityId: data.contactId },
          { entityType: 'company', entityId: data.companyId },
          { entityType: 'deal', entityId: data.dealId },
          { entityType: 'lead', entityId: data.leadId },
          { entityType: 'branch', entityId: data.branchId },
        ],
      }, apiRequest, {
        onAssociationError: () => {
          toast({
            title: "Invio registrato, collegamenti CRM incompleti",
            description: "Riapri la conversazione per verificare i collegamenti CRM e l'esito per destinatario.",
          });
        },
      });
    },
    onSuccess: (result) => {
      toast(canonicalEmailSendFeedback(result));

      clearRecoveredDraft();
      closeAfterSave();
      form.reset();
      setToInput("");
      setCcInput("");
      setBccInput("");
      setToRecipients([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setAttachments([]);

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails/counts"] });

      onSent?.(result);
    },
    onError: (error) => {
      toast({
        title: "Invio email non riuscito",
        description: userFacingApiError(error),
        variant: "destructive",
      });
    }
  });

  const submitComposer = async (data: EmailFormData, approval?: { draft: string; fingerprint: string }) => {
    const normalizedData = {
      ...data,
      to: splitEmailRecipients(toInput),
      cc: splitEmailRecipients(ccInput),
      bcc: splitEmailRecipients(bccInput),
    };
    normalizedData.to = toRecipients;
    normalizedData.cc = ccRecipients;
    normalizedData.bcc = bccRecipients;
    form.setValue("to", normalizedData.to);
    form.setValue("cc", normalizedData.cc);
    form.setValue("bcc", normalizedData.bcc);
    const saved = await saveDraftRef.current?.(false, true);
    if (!saved) {
      toast({ title: 'Invio sospeso', description: 'Salva prima la bozza e risolvi eventuali conflitti o problemi di rete.', variant: 'destructive' });
      return;
    }
    if (approval && composerReviewRef.current(approval.draft)?.fingerprint !== approval.fingerprint) {
      toast({ title: 'Invio sospeso', description: 'La composizione è cambiata durante il salvataggio. Ricontrolla destinatari, testo e allegati prima di confermare di nuovo.', variant: 'destructive' });
      return;
    }
    sendEmail.mutate(normalizedData);
  };
  const onSubmit = (data: EmailFormData) => submitComposer(data);

  const onInvalidSubmit = (errors: typeof form.formState.errors) => {
    const firstField = Object.keys(errors)[0];
    if (!firstField) return;
    requestAnimationFrame(() => {
      const modal = document.querySelector('[data-email-composer="true"]');
      const target = modal?.querySelector<HTMLElement>(`#${firstField}, [name="${firstField}"]`);
      target?.scrollIntoView({ block: "center", behavior: "smooth" });
      target?.focus();
    });
  };

  const saveDraft = async (manual = false, wait = false): Promise<boolean> => {
    if (!isRecoveryReady || sendEmail.isPending || draftSaveState === 'conflict' || isDraftInTrash) return false;
    if (isSavingDraftRef.current) {
      if (manual || wait) {
        await draftSavePromiseRef.current;
        return saveDraftRef.current?.(manual, wait) ?? false;
      }
      pendingDraftSaveRef.current = true;
      return false;
    }
    const data = form.getValues();
    if (!data.accountId) {
      setDraftSaveState('local');
      if (manual) toast({ title: "Account mittente mancante", description: "La copia locale resta recuperabile; seleziona un account per salvarla sul server.", variant: "destructive" });
      return false;
    }
    const signature = JSON.stringify({
      accountId: data.accountId, subject: data.subject, body: data.body,
      toRecipients, ccRecipients, bccRecipients, attachments,
      selectedTemplateId, selectedSignatureId, templateTrackOpens,
    });
    if (draftMessageIdRef.current && signature === lastSavedSignatureRef.current) return true;
    isSavingDraftRef.current = true;
    setIsSavingDraft(true);
    setDraftSaveState('saving');
    const work = (async (): Promise<boolean> => {
    try {
      const newAttachments = attachments.filter(attachment => attachment.contentBase64);
      const response = await apiRequest("/api/email/drafts", {
        method: 'POST', notifySuccess: false, notifyError: false,
        data: {
        ...(composerSourceMessageId ? { sourceMessageId: composerSourceMessageId } : {}),
        ...(draftMessageIdRef.current ? {
          draftMessageId: draftMessageIdRef.current,
          retainedAttachmentIds,
          ...(draftVersionRef.current ? { expectedUpdatedAt: draftVersionRef.current } : {}),
        } : {}),
        accountId: data.accountId,
        idempotencyKey: draftIdempotencyKeyRef.current ?? createEmailSendIdempotencyKey('draft'),
        threadId: composerThreadId ?? undefined,
        to: toRecipients,
        cc: ccRecipients,
        bcc: bccRecipients,
        subject: data.subject,
        ...(selectedTemplateId ? {
          templateId: selectedTemplateId,
          signatureId: selectedSignatureId === NO_SIGNATURE_VALUE ? null : Number(selectedSignatureId),
          trackOpens: templateTrackOpens,
        } : {}),
        text: stripHtml(`${stripEmailSignatureEditorMarkers(data.body || "")}${quotedEmailHtml}`),
        // Draft HTML is private editor state; retain signature ownership until send.
        html: `${data.body || ""}${quotedEmailHtml}`,
        attachments: newAttachments.map((attachment) => ({
          filename: attachment.filename,
          contentBase64: attachment.contentBase64!,
          contentType: attachment.contentType,
          size: attachment.size,
        })),
        },
      }) as any;
      if (!response?.message?.id) throw new Error("Salvataggio bozza non confermato dal server");
      draftMessageIdRef.current = Number(response.message.id);
      draftVersionRef.current = new Date(response.message.updatedAt).toISOString();
      lastSavedSignatureRef.current = signature;
      if (signature === JSON.stringify({
        accountId: form.getValues('accountId'), subject: form.getValues('subject'), body: form.getValues('body'),
        toRecipients, ccRecipients, bccRecipients, attachments,
        selectedTemplateId, selectedSignatureId, templateTrackOpens,
      })) {
        const persisted = persistedDraftAttachments({ id: Number(response.message.id), to: [], attachments: response.message.attachmentMetadata });
        if (persisted.length === attachments.length && attachments.some(attachment => attachment.contentBase64)) {
          setAttachments(persisted);
          initialPersistedAttachmentIdsRef.current = persisted.flatMap(attachment => attachment.persistedId ? [attachment.persistedId] : []);
        }
      }
      setDraftSaveState('saved');
      if (manual) toast({ title: "Bozza salvata", description: "Resta nella cartella Bozze. Nessuna email è stata inviata." });
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/messages", Number(response.message.id)] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails/counts"] });
      return true;
    } catch (error) {
      const message = userFacingApiError(error);
      setDraftSaveState(message.includes('altra scheda') || message.includes('un’altra scheda') ? 'conflict' : 'local');
      if (manual) toast({ title: "Salvataggio bozza non riuscito", description: message, variant: "destructive" });
      return false;
    } finally {
      isSavingDraftRef.current = false;
      setIsSavingDraft(false);
      draftSavePromiseRef.current = null;
      if (pendingDraftSaveRef.current) {
        pendingDraftSaveRef.current = false;
        window.setTimeout(() => { void saveDraftRef.current?.(false); }, 0);
      }
    }
    })();
    draftSavePromiseRef.current = work;
    return work;
  };
  saveDraftRef.current = saveDraft;

  useEffect(() => {
    if (!open || !isRecoveryReady || !selectedAccountId || isDraftInTrash || draftMessageIdRef.current || isSavingDraftRef.current) return;
    void saveDraftRef.current?.(false);
  }, [open, isRecoveryReady, selectedAccountId, isDraftInTrash]);

  useEffect(() => {
    if (!open || !isRecoveryReady || !selectedAccountId || isDraftInTrash) return;
    const signature = JSON.stringify({
      accountId: selectedAccountId, subject: watchedDraft.subject, body: watchedDraft.body,
      toRecipients, ccRecipients, bccRecipients, attachments,
      selectedTemplateId, selectedSignatureId, templateTrackOpens,
    });
    if (signature !== lastSavedSignatureRef.current && draftSaveState !== 'conflict') setDraftSaveState('local');
    const timeout = window.setTimeout(() => { void saveDraftRef.current?.(false); }, 800);
    return () => window.clearTimeout(timeout);
  }, [open, isRecoveryReady, selectedAccountId, watchedDraft.subject, watchedDraft.body,
    toInput, ccInput, bccInput, toRecipients, ccRecipients, bccRecipients, attachments,
    selectedTemplateId, selectedSignatureId, templateTrackOpens, isDraftInTrash]);

  useEffect(() => {
    if (!open || !isRecoveryReady || isDraftInTrash) return;
    const saveWhenHidden = () => {
      if (document.visibilityState === 'hidden') void saveDraftRef.current?.(false);
    };
    document.addEventListener('visibilitychange', saveWhenHidden);
    return () => document.removeEventListener('visibilitychange', saveWhenHidden);
  }, [open, isRecoveryReady, isDraftInTrash]);

  const deleteDraft = async () => {
    if (sendEmail.isPending) return;
    if (draftSavePromiseRef.current) await draftSavePromiseRef.current;
    const id = draftMessageIdRef.current;
    try {
      if (id) await apiRequest('PATCH', `/api/emails/${id}/trash`);
      clearRecoveredDraft();
      setIsDraftInTrash(true);
      closeAfterSave();
      queryClient.invalidateQueries({ queryKey: ['/api/emails'] });
      queryClient.invalidateQueries({ queryKey: ['/api/emails/counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/messages', id] });
      toast({ title: id ? 'Bozza nel Cestino' : 'Composizione eliminata', description: id ? 'La bozza è recuperabile dal Cestino.' : 'Nessuna email è stata inviata.' });
    } catch (error) {
      toast({ title: 'Eliminazione non riuscita', description: userFacingApiError(error), variant: 'destructive' });
    }
  };

  const restoreDraft = async () => {
    const id = draftMessageIdRef.current;
    if (!id) return;
    try {
      await apiRequest('PATCH', `/api/emails/${id}/restore`);
      const restored = await apiRequest(`/api/email/messages/${id}`) as any;
      draftVersionRef.current = restored?.updatedAt ? new Date(restored.updatedAt).toISOString() : null;
      setIsDraftInTrash(false);
      setDraftSaveState('saved');
      queryClient.invalidateQueries({ queryKey: ['/api/emails'] });
      queryClient.invalidateQueries({ queryKey: ['/api/emails/counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/messages', id] });
      toast({ title: 'Bozza ripristinata', description: 'Ora puoi modificarla o inviarla.' });
    } catch (error) {
      toast({ title: 'Ripristino non riuscito', description: userFacingApiError(error), variant: 'destructive' });
    }
  };

  const isLoading = isLoadingAccounts || isLoadingSignatures;
  const canSendEmail = !sendEmail.isPending && !isLoading && hasSendAccount && isRecoveryReady;
  const handleAddMissingContact = (email: string) => {
    openMissingEmailContactCandidate(email);
    toast({
      title: "Candidato contatto aperto",
      description: `${email} e' precompilato nel form contatto. Salva solo dopo aver verificato i dati.`,
    });
  };
  const insertMartinaDraft = (draft: string, mode: "append" | "replace") => {
    if (!draft.trim()) return;
    const currentBody = form.getValues("body") || "";
    const signature = selectedSignatureId === NO_SIGNATURE_VALUE || !Array.isArray(signatures)
      ? null
      : signatures.find((item) => item.id === Number(selectedSignatureId)) ?? null;
    const preparedDraft = trimMartinaDraftSignoffForSignature(draft, signature);
    const preparedDraftHtml = normalizeMartinaDraftForComposer(preparedDraft);
    const currentWithoutSignature = signature ? stripAppliedEmailSignature(currentBody) : currentBody;
    const insertedBody = mode === "replace"
      ? preparedDraftHtml
      : [currentWithoutSignature, preparedDraftHtml].filter((part) => part.trim()).join("<p><br></p>");
    const nextBody = signature ? applyEmailSignature(insertedBody, signature) : insertedBody;
    form.setValue("body", nextBody, { shouldDirty: true, shouldValidate: true });
    toast({
      title: "Bozza Martina inserita",
      description: mode === "replace" ? "Il corpo email e' stato sostituito." : "La proposta e' stata aggiunta al corpo email.",
    });
  };
  /**
   * Caso reale Rinaldi (2 ago 2026): "Inviala" nel drawer spediva una SECONDA
   * email costruita dal blocco della bozza (solo destinatario, oggetto, testo)
   * mentre l'email vera restava aperta nel composer con il suo allegato mai
   * partito. Martina confermava "email inviata" e il risultato era un successo
   * apparente. Quando la bozza nasce qui, l'invio deve passare da questo form:
   * e' l'unico che porta allegati, CC/BCC, deal, firma e citazione.
   */
  const preparedMartinaComposerBody = (draft: string) => {
    const signature = selectedSignatureId === NO_SIGNATURE_VALUE || !Array.isArray(signatures)
      ? null : signatures.find(item => item.id === Number(selectedSignatureId)) ?? null;
    const preparedDraft = trimMartinaDraftSignoffForSignature(draft, signature);
    const body = normalizeMartinaDraftForComposer(preparedDraft);
    return signature ? applyEmailSignature(body, signature) : body;
  };
  composerReviewRef.current = (draft) => {
    if (!open || !isRecoveryReady || !draft.trim()) return null;
    const data = form.getValues();
    const html = `${stripEmailSignatureEditorMarkers(preparedMartinaComposerBody(draft))}${quotedEmailHtml}`;
    return {
      fingerprint: JSON.stringify({ ...data, body: html, to: toRecipients, cc: ccRecipients, bcc: bccRecipients,
        attachments, draftMessageId: draftMessageIdRef.current, composerThreadId, composerSourceMessageId,
        selectedTemplateId, selectedSignatureId, templateTrackOpens }),
      from: data.from || '', to: [...toRecipients], cc: [...ccRecipients], bcc: [...bccRecipients],
      subject: data.subject || '', text: stripHtml(html), attachments: attachments.map(item => item.filename),
    };
  };
  const getComposerSendReview = async (draft: string): Promise<MartinaEmailComposerReview | null> => {
    // Persist attachment identity before presenting the review. Reading through
    // the ref after a render avoids stale closures captured when Genera ran.
    if (!await saveDraftRef.current?.(false, true)) return null;
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    return composerReviewRef.current(draft);
  };
  const sendMartinaDraftFromComposer = (draft: string, approvedFingerprint: string): boolean => {
    if (!canSendEmail || !approvedFingerprint || composerReviewRef.current(draft)?.fingerprint !== approvedFingerprint) {
      toast({ title: 'Invio sospeso', description: 'La composizione è cambiata o non è pronta. Ricontrolla l’email prima di confermare.', variant: 'destructive' });
      return false;
    }
    form.setValue('body', preparedMartinaComposerBody(draft), { shouldDirty: true, shouldValidate: true });
    void form.handleSubmit(data => submitComposer(data, { draft, fingerprint: approvedFingerprint }), onInvalidSubmit)();
    return true;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={requestEmailClose}>
      <DialogContent data-email-composer="true" className="email-reply-forward-modal flex h-[min(92dvh,780px)] w-[calc(100vw-2rem)] max-w-5xl flex-col overflow-hidden p-0 notion-dialog">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)} onBlurCapture={() => { if (isRecoveryReady) void saveDraftRef.current?.(false); }} className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
            <DialogHeader className="shrink-0 border-b border-border px-3 py-2.5 sm:px-4">
              <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <DialogTitle className="min-w-0 truncate text-sm font-semibold text-primary notion-title">
                  {modalTitle}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Componi, rispondi o inoltra una email scegliendo account mittente, destinatari, firma, oggetto e corpo del messaggio.
                </DialogDescription>
                <FormField
                  control={form.control}
                  name="accountId"
                  render={({ field }) => (
                    <FormItem className="min-w-0 lg:w-[20rem]">
                      <FormLabel className="sr-only">Account mittente</FormLabel>
                      <FormControl>
                        {accounts?.length ? (
                          <div className="flex min-w-0 items-center gap-2 text-xs">
                            <span className="shrink-0 text-muted-foreground">Da</span>
                            <select
                              disabled={!isRecoveryReady}
                              value={field.value ?? ""}
                              onChange={(event) => handleAccountChange(Number(event.target.value))}
                              className="h-8 min-w-0 flex-1 border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-primary"
                              aria-label="Account mittente"
                            >
                              {accounts.map((account: any) => {
                                const label = getAccountLabel(account);
                                const email = getAccountEmail(account);
                                return (
                                  <option key={account.id} value={account.id}>
                                    {email && email !== label ? `${label} - ${email}` : label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        ) : isLoadingAccounts ? (
                          <div className="border border-border px-2 py-1 text-xs text-muted-foreground">
                            Caricamento account...
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="border border-border bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              if (requestEmailClose(false)) navigate("/emails/accounts");
                            }}
                          >
                            Configura account
                          </button>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </DialogHeader>

            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
              {recoveryChoices.length > 0 ? <section className="space-y-3" aria-label="Bozze locali recuperabili">
                <p className="text-sm">Ci sono email non ancora salvate sul server. Scegli quale riprendere; nessuna email verra' inviata.</p>
                <div className="space-y-2">
                  {recoveryChoices.map(choice => <Button key={choice.key} type="button" variant="outline" className="h-auto min-h-11 w-full justify-start whitespace-normal break-words rounded-none text-left" onClick={() => restoreLocalDraft(choice.snapshot)}>
                    Recupera: {choice.snapshot.subject || 'Senza oggetto'}
                  </Button>)}
                </div>
                <Button type="button" variant="outline" className="min-h-11 rounded-none" onClick={() => { setRecoveryChoices([]); setIsRecoveryReady(true); }}>Continua con una nuova email</Button>
                <p className="text-xs text-muted-foreground">Le altre copie locali restano conservate su questo dispositivo.</p>
              </section> : <><div className="space-y-2.5">
              <FormField
                control={form.control}
                name="to"
                render={() => (
                  <FormItem className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                    <FormLabel className="mt-4 text-xs font-medium text-foreground">A</FormLabel>
                    <div className="min-w-0">
                      <CrmRecipientPicker
                        id="to"
                        label=""
                        value={toRecipients}
                        onChange={(emails) => {
                          setToRecipients(emails);
                          setToInput(emails.join(", "));
                          form.setValue("to", emails, { shouldValidate: true });
                        }}
                        knownEmails={initialCompose?.to ?? []}
                        placeholder="Cerca contatto, azienda, lead o email..."
                        onAddMissingContact={handleAddMissingContact}
                      />
                      {form.formState.errors.to && (
                        <p className="mt-1 text-xs font-medium text-destructive">
                          {form.formState.errors.to.message}
                        </p>
                      )}
                    </div>
                  </FormItem>
                )}
              />

              <div className="grid gap-2 lg:grid-cols-2">
                <FormItem className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                  <FormLabel className="mt-4 text-xs font-medium text-foreground">CC</FormLabel>
                  <div className="min-w-0">
                  <CrmRecipientPicker
                    id="cc"
                    label=""
                    value={ccRecipients}
                    onChange={(emails) => {
                      setCcRecipients(emails);
                      setCcInput(emails.join(", "));
                      form.setValue("cc", emails, { shouldValidate: true });
                    }}
                    placeholder="Cerca nel CRM..."
                    onAddMissingContact={handleAddMissingContact}
                  />
                  </div>
                </FormItem>

                <FormItem className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                  <FormLabel className="mt-4 text-xs font-medium text-foreground">BCC</FormLabel>
                  <div className="min-w-0">
                  <CrmRecipientPicker
                    id="bcc"
                    label=""
                    value={bccRecipients}
                    onChange={(emails) => {
                      setBccRecipients(emails);
                      setBccInput(emails.join(", "));
                      form.setValue("bcc", emails, { shouldValidate: true });
                    }}
                    placeholder="Cerca nel CRM..."
                    onAddMissingContact={handleAddMissingContact}
                  />
                  </div>
                </FormItem>
              </div>

              <div className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                <span className="pt-2 text-xs font-medium text-foreground">Modello</span>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                    <select
                      aria-label="Modello Email"
                      className="h-9 min-w-0 flex-1 border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-primary"
                      value={selectedTemplateId ?? ''}
                      onChange={(event) => applyTemplateSelection(event.target.value ? Number(event.target.value) : null)}
                      disabled={templatesQuery.isLoading}
                    >
                      <option value="">Nessun modello</option>
                      {(templatesQuery.data ?? []).map((template) => (
                        <option key={template.id} value={template.id}>{template.category} · {template.name}</option>
                      ))}
                    </select>
                    <Button type="button" variant="outline" className="h-9 shrink-0 rounded-none text-xs" onClick={() => setTemplateManagerOpen(true)}>
                      <LayoutTemplate className="mr-1.5 h-3.5 w-3.5" />Gestisci modelli
                    </Button>
                  </div>
                  {selectedTemplateId && (
                    <label className="mt-2 flex min-h-8 items-center gap-2 text-xs font-normal text-muted-foreground">
                      <input type="checkbox" className="h-4 w-4" checked={templateTrackOpens} onChange={(event) => setTemplateTrackOpens(event.target.checked)} />
                      Traccia aperture per questo invio
                    </label>
                  )}
                </div>
              </div>

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-center">
                    <FormLabel className="text-xs font-medium text-foreground">Oggetto</FormLabel>
                    <FormControl>
                      <Input className="h-9 rounded-none text-xs" placeholder="Oggetto email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dealId"
                render={({ field }) => (
                  <FormItem className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                    <FormLabel className="pt-2 text-xs font-medium text-foreground">Deal</FormLabel>
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-start gap-2">
                        {field.value ? (
                          <span className="inline-flex max-w-full items-center gap-1 border border-primary bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
                            <span className="truncate">
                              {selectedDeal ? dealLabel(selectedDeal) : `Deal #${field.value}`}
                            </span>
                            <button
                              type="button"
                              aria-label="Rimuovi deal dalla email"
                              className="shrink-0 text-primary-foreground/80 hover:text-primary-foreground"
                              onClick={() => {
                                field.onChange(null);
                                setDealSearch("");
                              }}
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null}
                        <div className="relative min-w-[220px] flex-1">
                          <Input
                            value={dealSearch}
                            onChange={(event) => setDealSearch(event.target.value)}
                            placeholder={field.value ? "Cambia deal..." : "Associa deal esistente..."}
                            className="h-8 rounded-none text-xs"
                          />
                          {matchingDealOptions.length > 0 && (
                            <div className="absolute left-0 right-0 top-9 z-50 max-h-48 overflow-y-auto border border-border bg-background shadow-lg">
                              {matchingDealOptions.map((deal) => (
                                <button
                                  key={deal.id}
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted"
                                  onClick={() => {
                                    field.onChange(Number(deal.id));
                                    setDealSearch("");
                                  }}
                                >
                                  <span className="truncate">{dealLabel(deal)}</span>
                                  <span className="text-xs font-normal text-muted-foreground">#{deal.id}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Le reply ereditano il deal della conversazione; puoi rimuoverlo con la X prima di inviare.
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <EmailMartinaComposerBar
                getCurrentBody={() => form.getValues("body") || ""}
                getSubject={() => form.getValues("subject") || ""}
                to={toRecipients}
                mode={modalMode}
                emailMessageId={replyTo?.id ?? forwardTo?.id ?? null}
                onInsertDraft={insertMartinaDraft}
                onSendDraft={sendMartinaDraftFromComposer}
                getSendReview={getComposerSendReview}
              />

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          preserveEmailSignatureBoundaries
                          placeholder="Scrivi il messaggio..."
                          className="min-h-[220px] border rounded-none bg-background p-3 text-xs"
                        />
                        {quotedEmailHtml && (
                          <div
                            className="mt-2 min-w-0 max-w-full overflow-x-auto border-l-2 border-border bg-muted/20 px-3 py-2 text-xs text-foreground [&_img]:max-w-full [&_img]:h-auto"
                            data-testid="email-original-quote"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quotedPreviewHtml) }}
                          />
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <fieldset className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:items-start">
                <legend className="sr-only">Firma email</legend>
                <span className="pt-2 text-xs font-medium text-foreground">Firma</span>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap gap-1.5">
                    {isLoadingSignatures ? (
                      <div className="border border-border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                        Caricamento firme...
                      </div>
                    ) : signatureOptions.map((signature) => {
                      const selected = selectedSignatureId === signature.id;
                      return (
                        <button
                          key={signature.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => {
                            manualSignatureSelectionRef.current = true;
                            applySignatureSelection(signature.id);
                          }}
                          className={[
                            "min-w-0 border px-2 py-1 text-left text-xs transition-colors",
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background text-foreground hover:border-primary",
                          ].join(" ")}
                        >
                          <span className="block max-w-[14rem] truncate font-medium">{signature.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </fieldset>
            </div>

            {/* Allegati */}
            <div className="mt-3 border-t border-border pt-3">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleAttachFiles(e.target.files)}
              />
              {attachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {attachments.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1 border border-border bg-muted px-2 py-1 text-xs text-foreground">
                      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="max-w-[140px] truncate">{a.filename}</span>
                      <span className="text-muted-foreground">({formatAttachmentSize(a.size)})</span>
                      <button type="button" aria-label="Rimuovi allegato" onClick={() => setAttachments((p) => p.filter((x) => x.id !== a.id))} className="ml-1 text-muted-foreground hover:text-destructive">
                        <XIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Allega file
              </button>
            </div></>}
            </div>

            <DialogFooter className="shrink-0 border-t border-border px-3 py-2.5 sm:px-4">
              <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-xs text-muted-foreground">
                  {form.formState.isSubmitted && Object.keys(form.formState.errors).length > 0 ? (
                    <p role="alert" className="font-medium text-destructive">
                      {String(Object.values(form.formState.errors)[0]?.message || "Controlla i campi obbligatori prima di inviare.")}
                    </p>
                  ) : null}
                  <p aria-live="polite">{isDraftInTrash ? 'Bozza nel Cestino' : draftSaveState === 'saving' ? 'Salvataggio bozza…' : draftSaveState === 'saved' ? 'Bozza salvata' : draftSaveState === 'conflict' ? 'Conflitto: riapri la bozza' : 'Non ancora salvata sul server'} · {attachments.length > 0 ? `${attachments.length} allegati` : 'Nessun allegato'} · Invio manuale</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-none sm:w-auto"
                onClick={() => {
                  if (!requestEmailClose(false)) return;
                }}
              >
                Annulla
              </Button>
              {isDraftInTrash ? <Button type="button" variant="outline" className="w-full rounded-none sm:w-auto" onClick={() => void restoreDraft()}>Ripristina bozza</Button> : <Button
                type="button"
                variant="outline"
                className="w-full rounded-none sm:w-auto"
                onClick={() => void saveDraft(true)}
                disabled={!isRecoveryReady || isSavingDraft || sendEmail.isPending || isLoading || !hasSendAccount || draftSaveState === 'conflict'}
                title={hasSendAccount ? "Salva la composizione nella cartella Bozze" : "Configura un account Email prima di salvare"}
              >
                {isSavingDraft ? "Salvataggio..." : "Salva bozza"}
              </Button>}
              {!isDraftInTrash && <Button type="button" variant="destructive" className="w-full rounded-none sm:w-auto" onClick={() => void deleteDraft()} disabled={!isRecoveryReady || sendEmail.isPending}>Elimina</Button>}
              <Button
                type="submit"
                disabled={!canSendEmail || isDraftInTrash || draftSaveState === 'conflict'}
                className="col-span-2 w-full rounded-none sm:w-auto"
                onClick={processRecipients}
                title={hasSendAccount ? "Invia l'email al destinatario" : "Configura un account Email prima di inviare"}
              >
                {sendEmail.isPending ? "Invio in corso..." : !hasSendAccount ? "Configura account" : submitLabel}
              </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <EmailTemplateManagerDialog open={templateManagerOpen} onOpenChange={setTemplateManagerOpen} />
    </>
  );
}
